// src/pages/lms/LMSClasses.jsx — V3 FINAL
// ROOT CAUSES FIXED:
// 1. TYPING LAG: Class form & student form modals are now separate
//    isolated components (ClassFormModal, StudentFormModal)
//    They have their OWN local state — parent never re-renders on keystroke
// 2. CLASS NOT SAVING: Removed institution_account_id (column may not exist)
//    Now only sends columns that definitely exist in lms_classes
// 3. CLASS NOT SHOWING: uses .neq('is_active',false) to catch null values too

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import { useNavigate } from 'react-router-dom';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import {
  Plus, X, Trash2, Edit, Search, Users, UserPlus,
  ChevronRight, BookOpen, Phone, UserCheck,
} from 'lucide-react';

const DEGREES     = ['FSc','ICS','FA','BS','BE','BBA','MS','MBA','PhD','Other'];
const SHIFTS      = ['morning','afternoon','evening'];
const DEPARTMENTS = ['Computer Science','Business Administration','Engineering',
  'Mathematics','Physics','Chemistry','Biology','English','Urdu','Islamic Studies',
  'Social Sciences','Economics','Law','Medical','Architecture','Arts','Other'];
const ENROLLMENT_STATUS = ['active','inactive','transferred','graduated'];
const INP_STYLE = { width:'100%', padding:'9px 12px', borderRadius:8,
  border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box',
  outline:'none', color:'#111827' };

// ─────────────────────────────────────────────────────
// CLASS FORM MODAL — own local state, zero parent re-renders on typing
// ─────────────────────────────────────────────────────
function ClassFormModal({ editClass, teachers, institutionId, navigate, onSave, onClose }) {
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  // Selects need controlled state (no typing lag on selects, only on text inputs)
  const [dept,      setDept]      = useState(editClass?.department||'');
  const [degree,    setDegree]    = useState(editClass?.degree||'BS');
  const [year,      setYear]      = useState(editClass?.year_of_study||'');
  const [shift,     setShift]     = useState(editClass?.shift||'morning');
  const [teacherId, setTeacherId] = useState(editClass?.class_teacher_id||'');
  // Text inputs — uncontrolled refs, ZERO re-renders
  const nameRef    = useRef(null);
  const dispRef    = useRef(null);
  const sectionRef = useRef(null);
  const semRef     = useRef(null);
  const yearStrRef = useRef(null);
  const roomRef    = useRef(null);

  const selectedTeacher = teachers.find(t=>t.id===teacherId);

  const save = async () => {
    const name = nameRef.current?.value?.trim()||'';
    if (!name) { setError('Class name is required'); return; }
    setSaving(true); setError('');
    try {
      const data = {
        name,
        display_name:       dispRef.current?.value?.trim()||null,
        department:         dept||null,
        degree:             degree||null,
        year_of_study:      year ? Number(year) : null,
        section:            sectionRef.current?.value?.trim()||null,
        semester:           semRef.current?.value?.trim()||null,
        academic_year:      yearStrRef.current?.value?.trim()||null,
        shift:              shift||'morning',
        room:               roomRef.current?.value?.trim()||null,
        class_teacher_id:   teacherId||null,
        class_teacher_name: selectedTeacher?.full_name||'',
        institution_id:     institutionId,
        is_active:          true,
      };
      if (editClass) {
        const { error: e } = await supabase.from('lms_classes').update(data).eq('id',editClass.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase.from('lms_classes').insert(data);
        if (e) throw e;
      }
      onSave(editClass ? 'Class updated!' : 'Class created!');
    } catch(e) {
      setError(e.message?.includes('unique') ? 'A class with this name already exists.' : (e.message||'Save failed. Check browser console.'));
      console.error('saveClass error:', e);
    } finally { setSaving(false); }
  };

  const F = ({label,children,col}) => (
    <div style={{gridColumn:col||'span 1'}}>
      <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:640,maxHeight:'92vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,color:'#111827',fontSize:18}}>{editClass?'Edit Class':'Create Class'}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B',fontWeight:600}}>{error}</div>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <F label="Class Name *" col="span 2">
              {/* uncontrolled — no lag */}
              <input ref={nameRef} defaultValue={editClass?.name||''} placeholder="e.g. BS-CS-6A or Class 10-A" style={INP_STYLE}/>
            </F>
            <F label="Display Name">
              <input ref={dispRef} defaultValue={editClass?.display_name||''} placeholder="e.g. Batch 2021 Section A" style={INP_STYLE}/>
            </F>
            <F label="Department">
              <select value={dept} onChange={e=>setDept(e.target.value)} style={INP_STYLE}>
                <option value="">Select...</option>
                {DEPARTMENTS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </F>
            <F label="Degree">
              <select value={degree} onChange={e=>setDegree(e.target.value)} style={INP_STYLE}>
                {DEGREES.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </F>
            <F label="Year of Study">
              <select value={year} onChange={e=>setYear(e.target.value)} style={INP_STYLE}>
                <option value="">Select...</option>
                {[1,2,3,4,5,6].map(y=><option key={y} value={y}>Year {y}</option>)}
              </select>
            </F>
            <F label="Section">
              <input ref={sectionRef} defaultValue={editClass?.section||''} placeholder="e.g. A, B, C" style={INP_STYLE}/>
            </F>
            <F label="Shift">
              <select value={shift} onChange={e=>setShift(e.target.value)} style={INP_STYLE}>
                {SHIFTS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </F>
            <F label="Semester">
              <input ref={semRef} defaultValue={editClass?.semester||''} placeholder="e.g. Fall 2025, Semester 6" style={INP_STYLE}/>
            </F>
            <F label="Academic Year">
              <input ref={yearStrRef} defaultValue={editClass?.academic_year||''} placeholder="e.g. 2024-25" style={INP_STYLE}/>
            </F>
            <F label="Room / Location">
              <input ref={roomRef} defaultValue={editClass?.room||''} placeholder="e.g. Room 201, Block A" style={INP_STYLE}/>
            </F>
            <F label="Class Teacher" col="span 2">
              <select value={teacherId} onChange={e=>setTeacherId(e.target.value)}
                style={{...INP_STYLE, borderColor:teacherId?'#7C3AED':'#E5E7EB'}}>
                <option value="">— No class teacher assigned —</option>
                {teachers.map(t=>(
                  <option key={t.id} value={t.id}>
                    {t.full_name}{t.designation?` (${t.designation})`:''}
                  </option>
                ))}
              </select>
              {teachers.length===0 && (
                <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:12,color:'#9CA3AF'}}>No teachers added yet.</span>
                  <button type="button" onClick={()=>{onClose();navigate('/lms/teachers');}}
                    style={{fontSize:12,fontWeight:700,color:'#4F8EF7',background:'#EFF6FF',padding:'4px 10px',borderRadius:6,border:'1px solid #BFDBFE',cursor:'pointer'}}>
                    + Add Teacher First →
                  </button>
                </div>
              )}
              {teachers.length>0 && !teacherId && (
                <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                  <span style={{fontSize:12,color:'#F59E0B'}}>⚠️ Without a class teacher, attendance cannot be marked.</span>
                  <button type="button" onClick={()=>{onClose();navigate('/lms/teachers');}}
                    style={{fontSize:12,fontWeight:700,color:'#4F8EF7',background:'#EFF6FF',padding:'4px 10px',borderRadius:6,border:'1px solid #BFDBFE',cursor:'pointer',whiteSpace:'nowrap'}}>
                    Add New Teacher →
                  </button>
                </div>
              )}
              {teacherId && selectedTeacher && (
                <div style={{marginTop:6,fontSize:12,color:'#5B21B6',fontWeight:600}}>
                  ✅ {selectedTeacher.full_name} assigned as class teacher.
                  Set their attendance PIN in Attendance → Settings.
                </div>
              )}
            </F>
          </div>
          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={onClose}
              style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer',fontSize:13}}>
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
              {saving ? 'Saving...' : editClass ? 'Update Class' : 'Create Class'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// STUDENT FORM MODAL — own local state, zero parent re-renders
// ─────────────────────────────────────────────────────
function StudentFormModal({ editStud, classId, institutionId, onSave, onClose }) {
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [bulkTab,   setBulkTab]   = useState('single');
  const [rollResult,setRollResult]= useState(null);
  const [rollLoad,  setRollLoad]  = useState(false);
  const [degree,    setDegree]    = useState(editStud?.student_degree||'');
  const [studYear,  setStudYear]  = useState(editStud?.student_year||'');
  const [status,    setStatus]    = useState(editStud?.enrollment_status||'active');
  // Controlled state for fields that need to update after roll lookup
  const [sName,  setSName]  = useState(editStud?.student_name||'');
  const [sRoll,  setSRoll]  = useState(editStud?.student_roll_number||'');
  const [sUser,  setSUser]  = useState(editStud?.student_username||'');
  const [sPhone, setSPhone] = useState(editStud?.student_phone||'');
  const [sEmail, setSEmail] = useState(editStud?.student_email||'');
  const [sNotes, setSNotes] = useState(editStud?.notes||'');
  // Uncontrolled refs (bulk + lookup only)
  const bulkRef  = useRef(null);
  const lookupRef= useRef(null);

  const lookupRoll = async () => {
    const roll = lookupRef.current?.value?.trim()||'';
    if (!roll) return;
    setRollLoad(true); setRollResult(null);
    try {
      // First try by roll_no RPC
      const { data, error: rpcErr } = await supabase.rpc('find_student_by_roll_no', {
        p_institution_id: institutionId,
        p_roll_no: roll,
      });
      console.log('Roll lookup result:', data, rpcErr);

      // RPC returns jsonb — data is the object directly
      const result = data;

      if (result && result.found) {
        // Use setState — controlled inputs update instantly
        setSName(result.full_name   || '');
        setSRoll(result.roll_no     || roll);
        setSUser(result.username    || '');
        setSEmail(result.email      || '');
        setSPhone(result.phone      || '');
        setDegree(result.degree     || '');
        setStudYear(result.year_of_study || '');
        setRollResult(result);
      } else {
        // Fallback — query profiles table directly
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id,full_name,username,roll_no,degree,year_of_study,email,phone')
          .eq('institution_id', institutionId)
          .ilike('roll_no', roll)
          .eq('role', 'student')
          .maybeSingle();
        console.log('Profile fallback:', profileData);
        if (profileData) {
          setSName(profileData.full_name        || '');
          setSRoll(profileData.roll_no          || roll);
          setSUser(profileData.username         || '');
          setSEmail(profileData.email           || '');
          setSPhone(profileData.phone           || '');
          setDegree(profileData.degree          || '');
          setStudYear(profileData.year_of_study || '');
          setRollResult({ found: true, ...profileData });
        } else {
          setRollResult({ found: false });
        }
      }
    } catch(e) {
      console.error('lookupRoll error:', e);
      setRollResult({ found: false });
    }
    finally { setRollLoad(false); }
  };

  const save = async () => {
    if (!sName.trim()) { setError('Student name is required'); return; }
    setSaving(true); setError('');
    try {
      const roll = sRoll.trim()||null;
      const data = {
        class_id:            classId,
        institution_id:      institutionId,
        student_name:        sName.trim(),
        student_roll_number: sRoll.trim()||null,
        student_username:    sUser.trim()||null,
        student_phone:       sPhone.trim()||null,
        student_email:       sEmail.trim()||null,
        student_degree:      degree||null,
        student_year:        studYear?Number(studYear):null,
        notes:               sNotes.trim()||null,
        enrollment_status:   status,
      };
      if (editStud) {
        const { error:e } = await supabase.from('lms_class_students').update(data).eq('id',editStud.id);
        if (e) throw e;
      } else {
        const { error:e } = await supabase.from('lms_class_students').insert(data);
        if (e) throw e;
      }
      onSave(editStud ? 'Student updated!' : 'Student enrolled!');
    } catch(e) {
      setError(e.message?.includes('unique') ? 'This roll number is already enrolled in this class.' : (e.message||'Save failed'));
      console.error('saveStudent error:', e);
    } finally { setSaving(false); }
  };

  const bulkAdd = async () => {
    const text  = bulkRef.current?.value||'';
    const lines = text.trim().split('\n').filter(l=>l.trim());
    if (!lines.length) { setError('Enter at least one student'); return; }
    setSaving(true); setError('');
    let added=0, skipped=0;
    for (const line of lines) {
      const parts  = line.split(',').map(p=>p.trim());
      const hasRoll= parts.length>=2 && /\d/.test(parts[0]);
      const row = {
        class_id:            classId,
        institution_id:      institutionId,
        student_roll_number: hasRoll ? parts[0] : null,
        student_name:        hasRoll ? parts.slice(1).join(' ') : parts[0],
        enrollment_status:   'active',
      };
      if (!row.student_name) continue;
      try { await supabase.from('lms_class_students').insert(row); added++; }
      catch(_){ skipped++; }
    }
    onSave(`✅ Added ${added} students${skipped>0?`, ${skipped} skipped (already enrolled)`:''}`);
  };

  const F = ({label,children,col}) => (
    <div style={{gridColumn:col||'span 1'}}>
      <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:600,maxHeight:'94vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,color:'#111827',fontSize:18}}>{editStud?'Edit Student':'Enroll Student'}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B',fontWeight:600}}>{error}</div>}
          {!editStud && (
            <div style={{display:'flex',background:'#F1F5F9',borderRadius:10,padding:3,marginBottom:18,gap:2}}>
              {[{k:'single',l:'Single Student'},{k:'bulk',l:'Bulk Add'}].map(t=>(
                <button key={t.k} onClick={()=>setBulkTab(t.k)}
                  style={{flex:1,padding:'8px 0',borderRadius:8,border:'none',background:bulkTab===t.k?'#fff':'transparent',color:bulkTab===t.k?'#1E293B':'#64748B',fontWeight:700,fontSize:13,cursor:'pointer',boxShadow:bulkTab===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none'}}>
                  {t.l}
                </button>
              ))}
            </div>
          )}

          {bulkTab==='bulk' && !editStud ? (
            <div>
              <div style={{fontSize:12,color:'#6B7280',marginBottom:10,background:'#F8FAFF',borderRadius:8,padding:'10px 12px',lineHeight:1.7}}>
                <strong>Format — one per line:</strong><br/>
                With roll no: <code>2024-CS-001, Ahmed Ali</code><br/>
                Without roll no: <code>Ahmed Ali</code>
              </div>
              <textarea ref={bulkRef} rows={10}
                placeholder={"2024-CS-001, Ahmed Ali\n2024-CS-002, Sara Khan\n2024-CS-003, Usman Raza\n..."}
                style={{...INP_STYLE,resize:'vertical',fontFamily:'monospace',fontSize:12}}/>
              <button onClick={bulkAdd} disabled={saving}
                style={{marginTop:14,width:'100%',padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)',color:saving?'#9CA3AF':'#fff',fontWeight:700,fontSize:14,cursor:saving?'wait':'pointer'}}>
                {saving ? 'Adding...' : '➕ Bulk Add Students'}
              </button>
            </div>
          ) : (
            <div>
              {!editStud && (
                <div style={{background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)',border:'1px solid #BFDBFE',borderRadius:12,padding:14,marginBottom:18}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#1D4ED8',marginBottom:8}}>🔍 Lookup by Roll Number (auto-fills details from app)</div>
                  <div style={{display:'flex',gap:8}}>
                    <input ref={lookupRef} defaultValue="" onKeyDown={e=>e.key==='Enter'&&lookupRoll()} placeholder="Enter roll number to auto-fill..." style={{...INP_STYLE,flex:1}}/>
                    <button onClick={lookupRoll} disabled={rollLoad}
                      style={{padding:'9px 16px',borderRadius:8,border:'none',background:'#4F8EF7',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
                      {rollLoad ? '...' : 'Lookup'}
                    </button>
                  </div>
                  {rollResult && (
                    <div style={{marginTop:8,fontSize:12,color:rollResult.found?'#065F46':'#991B1B',fontWeight:600}}>
                      {rollResult.found ? `✅ Found: ${rollResult.full_name} · Details auto-filled below` : '❌ Not found in app. Fill details manually.'}
                    </div>
                  )}
                </div>
              )}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                <F label="Full Name *" col="span 2">
                  <input value={sName} onChange={e=>setSName(e.target.value)} placeholder="Student full name" style={INP_STYLE}/>
                </F>
                <F label="Roll Number">
                  <input value={sRoll} onChange={e=>setSRoll(e.target.value)} placeholder="e.g. 2024-CS-001" style={INP_STYLE}/>
                </F>
                <F label="Username (app)">
                  <input value={sUser} onChange={e=>setSUser(e.target.value)} placeholder="App username" style={INP_STYLE}/>
                </F>
                <F label="Phone">
                  <input value={sPhone} onChange={e=>setSPhone(e.target.value)} placeholder="03xx-xxxxxxx" style={INP_STYLE}/>
                </F>
                <F label="Email">
                  <input value={sEmail} onChange={e=>setSEmail(e.target.value)} placeholder="student@email.com" style={INP_STYLE}/>
                </F>
                <F label="Degree">
                  <select value={degree} onChange={e=>setDegree(e.target.value)} style={INP_STYLE}>
                    <option value="">Select...</option>
                    {DEGREES.map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </F>
                <F label="Year of Study">
                  <select value={studYear} onChange={e=>setStudYear(e.target.value)} style={INP_STYLE}>
                    <option value="">Select...</option>
                    {[1,2,3,4,5,6].map(y=><option key={y} value={y}>Year {y}</option>)}
                  </select>
                </F>
                <F label="Enrollment Status" col="span 2">
                  <select value={status} onChange={e=>setStatus(e.target.value)} style={INP_STYLE}>
                    {ENROLLMENT_STATUS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                  </select>
                </F>
                <F label="Notes" col="span 2">
                  <input value={sNotes} onChange={e=>setSNotes(e.target.value)} placeholder="Optional notes..." style={INP_STYLE}/>
                </F>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20}}>
                <button onClick={onClose}
                  style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>
                  Cancel
                </button>
                <button onClick={save} disabled={saving}
                  style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
                  {saving ? 'Saving...' : editStud ? 'Update Student' : 'Enroll Student'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// STUDENT ROW — isolated component, status change is instant
// ─────────────────────────────────────────────────────
function StudentRow({ s, i, onEdit, onRemove, onStatusChange }) {
  const SC = { active:{bg:'#F0FDF4',color:'#065F46',border:'#BBF7D0'}, inactive:{bg:'#F9FAFB',color:'#6B7280',border:'#E5E7EB'}, transferred:{bg:'#EFF6FF',color:'#1D4ED8',border:'#BFDBFE'}, graduated:{bg:'#F5F3FF',color:'#5B21B6',border:'#DDD6FE'} };
  const [localStatus, setLocalStatus] = useState(s.enrollment_status);
  const c = SC[localStatus]||SC.active;
  return (
    <tr style={{borderBottom:'1px solid #F1F5F9'}}>
      <td style={{padding:'10px 14px',fontSize:11,color:'#9CA3AF',fontWeight:600}}>{i+1}</td>
      <td style={{padding:'10px 14px'}}>
        {s.student_roll_number
          ? <code style={{fontSize:11,fontWeight:800,color:'#4F8EF7',background:'#EFF6FF',padding:'2px 7px',borderRadius:4}}>{s.student_roll_number}</code>
          : <span style={{color:'#D1D5DB',fontSize:11}}>—</span>}
      </td>
      <td style={{padding:'10px 14px'}}>
        <div style={{fontWeight:700,color:'#111827',fontSize:13}}>{s.student_name}</div>
        {s.student_username && <div style={{fontSize:10,color:'#9CA3AF'}}>@{s.student_username}</div>}
        {s.notes && <div style={{fontSize:9,color:'#D97706'}}>📝 {s.notes}</div>}
      </td>
      <td style={{padding:'10px 14px'}}>
        {s.student_degree && <div style={{fontSize:12,color:'#374151',fontWeight:600}}>{s.student_degree}</div>}
        {s.student_year   && <div style={{fontSize:10,color:'#9CA3AF'}}>Year {s.student_year}</div>}
      </td>
      <td style={{padding:'10px 14px'}}>
        {s.student_phone && <div style={{fontSize:11,color:'#374151',display:'flex',alignItems:'center',gap:3}}><Phone size={9} color="#9CA3AF"/>{s.student_phone}</div>}
        {s.student_email && <div style={{fontSize:10,color:'#9CA3AF',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.student_email}</div>}
      </td>
      <td style={{padding:'10px 14px'}}>
        {s.student_id
          ? <span style={{fontSize:9,fontWeight:700,color:'#10B981',background:'#F0FDF4',padding:'2px 7px',borderRadius:4,border:'1px solid #BBF7D0'}}>✅ App</span>
          : <span style={{fontSize:9,color:'#9CA3AF'}}>—</span>}
      </td>
      <td style={{padding:'10px 14px'}}>
        <select value={localStatus}
          onChange={e=>{ setLocalStatus(e.target.value); onStatusChange(s.id, e.target.value); }}
          style={{fontSize:10,fontWeight:700,color:c.color,background:c.bg,border:`1px solid ${c.border}`,padding:'2px 6px',borderRadius:5,cursor:'pointer',outline:'none'}}>
          {ENROLLMENT_STATUS.map(st=><option key={st} value={st}>{st.charAt(0).toUpperCase()+st.slice(1)}</option>)}
        </select>
      </td>
      <td style={{padding:'8px 12px'}}>
        <div style={{display:'flex',gap:4}}>
          <button onClick={()=>onEdit(s)} style={{padding:'4px 8px',borderRadius:5,border:'1px solid #E5E7EB',background:'#F9FAFB',cursor:'pointer'}}><Edit size={11} color="#374151"/></button>
          <button onClick={()=>onRemove(s.id,s.student_name)} style={{padding:'4px 8px',borderRadius:5,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}><Trash2 size={11} color="#EF4444"/></button>
        </div>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────
// MAIN COMPONENT — minimal state, no form state here
// ─────────────────────────────────────────────────────
export default function LMSClasses() {
  const { institution } = useLMSAuth();
  const navigate = useNavigate();
  const [view,    setView]    = useState('list');
  const [classes, setClasses] = useState([]);
  const [teachers,setTeachers]= useState([]);
  const [selClass,setSelClass]= useState(null);
  const [students,setStudents]= useState([]);
  const [loading, setLoading] = useState(true);
  const [studLoad,setStudLoad]= useState(false);
  const [showClassForm,   setShowClassForm]   = useState(false);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [editStud,  setEditStud]  = useState(null);
  const [success,   setSuccess]   = useState('');
  const [studStatus,setStudStatus]= useState('active');
  // Debounced searches — only these live in parent
  const [classSearchInput, setClassSearchInput] = useState('');
  const [classSearch,      setClassSearch]      = useState('');
  const [studSearchInput,  setStudSearchInput]  = useState('');
  const [studSearch,       setStudSearch]       = useState('');
  const csTimer = useRef(null);
  const ssTimer = useRef(null);
  const handleClassSearch = v=>{ setClassSearchInput(v); clearTimeout(csTimer.current); csTimer.current=setTimeout(()=>setClassSearch(v),200); };
  const handleStudSearch  = v=>{ setStudSearchInput(v);  clearTimeout(ssTimer.current); ssTimer.current=setTimeout(()=>setStudSearch(v),200); };

  useEffect(()=>{ if(institution?.id) loadAll(); },[institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        supabase.from('lms_classes')
          .select('*').eq('institution_id',institution.id)
          .neq('is_active',false).order('name'),
        supabase.from('lms_teachers')
          .select('id,full_name,designation,department')
          .eq('institution_id',institution.id).eq('status','active').order('full_name'),
      ]);
      if (cRes.error) console.error('loadClasses error:',cRes.error);
      if (tRes.error) console.error('loadTeachers error:',tRes.error);
      setClasses(cRes.data||[]);
      setTeachers(tRes.data||[]);
    } catch(e){ console.error('loadAll error:',e); }
    finally{ setLoading(false); }
  };

  const loadStudents = useCallback(async(classId)=>{
    setStudLoad(true);
    try {
      const { data,error } = await supabase.from('lms_class_students')
        .select('*').eq('class_id',classId)
        .order('student_roll_number',{ascending:true,nullsFirst:false});
      if (error) console.error('loadStudents error:',error);
      setStudents(data||[]);
    } catch(e){ console.error(e); }
    finally{ setStudLoad(false); }
  },[]);

  const openClass = cls=>{ setSelClass(cls); setView('detail'); setStudSearchInput(''); setStudSearch(''); setStudStatus('active'); loadStudents(cls.id); };

  const handleSaveClass = async(msg)=>{ setSuccess(msg); setShowClassForm(false); setEditClass(null); await loadAll(); setTimeout(()=>setSuccess(''),4000); };
  const handleSaveStud  = async(msg)=>{ setSuccess(msg); setShowStudentForm(false); setEditStud(null); await loadStudents(selClass.id); setTimeout(()=>setSuccess(''),4000); };

  const removeStudent = async(id,name)=>{
    if (!window.confirm(`Remove ${name} from this class?`)) return;
    await supabase.from('lms_class_students').delete().eq('id',id);
    await loadStudents(selClass.id);
  };
  const updateStudentStatus = async(id,status)=>{
    await supabase.from('lms_class_students').update({enrollment_status:status}).eq('id',id);
  };

  const filteredClasses = useMemo(()=>classes.filter(c=>{
    if (!classSearch.trim()) return true;
    const q=classSearch.toLowerCase();
    return (c.name||'').toLowerCase().includes(q)||(c.department||'').toLowerCase().includes(q)||(c.class_teacher_name||'').toLowerCase().includes(q);
  }),[classes,classSearch]);

  const filteredStudents = useMemo(()=>students.filter(s=>{
    const mSt = !studStatus||s.enrollment_status===studStatus;
    if (!mSt) return false;
    if (!studSearch.trim()) return true;
    const q=studSearch.toLowerCase();
    return (s.student_name||'').toLowerCase().includes(q)||(s.student_roll_number||'').toLowerCase().includes(q)||(s.student_username||'').toLowerCase().includes(q)||(s.student_email||'').toLowerCase().includes(q)||(s.student_phone||'').toLowerCase().includes(q);
  }),[students,studSearch,studStatus]);

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="classes">

        {success && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}

        {/* DETAIL VIEW */}
        {view==='detail' && selClass ? (
          <>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
              <button onClick={()=>{setView('list');setSelClass(null);setStudents([]);}}
                style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>← Classes</button>
              <div style={{flex:1}}>
                <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>{selClass.name}</h1>
                <div style={{fontSize:13,color:'#6B7280',marginTop:2,display:'flex',gap:12,flexWrap:'wrap'}}>
                  {selClass.degree     && <span>🎓 {selClass.degree}</span>}
                  {selClass.department && <span>📚 {selClass.department}</span>}
                  {selClass.class_teacher_name
                    ? <span style={{color:'#7C3AED',fontWeight:700}}>👨‍🏫 {selClass.class_teacher_name} (Class Teacher)</span>
                    : <span style={{color:'#EF4444'}}>⚠️ No class teacher assigned</span>}
                  {selClass.room && <span>🏛 Room {selClass.room}</span>}
                  <span>👥 {students.filter(s=>s.enrollment_status==='active').length} active</span>
                </div>
              </div>
              <button onClick={()=>{setShowStudentForm(true);setEditStud(null);}}
                style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#10B981,#059669)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                <UserPlus size={16}/> Enroll Student
              </button>
            </div>

            {!selClass.class_teacher_id && (
              <div style={{background:'#FFF7ED',border:'1.5px solid #FED7AA',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:20}}>⚠️</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,color:'#92400E',fontSize:13}}>No Class Teacher Assigned</div>
                  <div style={{fontSize:12,color:'#B45309'}}>Assign a class teacher so they can mark attendance.</div>
                </div>
                <button onClick={()=>{setEditClass(selClass);setShowClassForm(true);}}
                  style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#F59E0B',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                  Assign Teacher
                </button>
              </div>
            )}

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))',gap:10,marginBottom:16}}>
              {[{l:'Total',v:students.length,c:'#4F8EF7'},{l:'Active',v:students.filter(s=>s.enrollment_status==='active').length,c:'#10B981'},{l:'Inactive',v:students.filter(s=>s.enrollment_status==='inactive').length,c:'#6B7280'},{l:'Transferred',v:students.filter(s=>s.enrollment_status==='transferred').length,c:'#3B82F6'},{l:'Graduated',v:students.filter(s=>s.enrollment_status==='graduated').length,c:'#7C3AED'}].map(s=>(
                <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'12px 14px'}}>
                  <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:10,color:'#6B7280'}}>{s.l}</div>
                </div>
              ))}
            </div>

            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              <div style={{position:'relative',flex:1,minWidth:180}}>
                <Search size={13} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                <input value={studSearchInput} onChange={e=>handleStudSearch(e.target.value)}
                  placeholder="Name, roll no, email, phone..." style={{...INP_STYLE,paddingLeft:28}}/>
              </div>
              <select value={studStatus} onChange={e=>setStudStatus(e.target.value)} style={{...INP_STYLE,width:'auto'}}>
                <option value="">All Status</option>
                {ENROLLMENT_STATUS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
              <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filteredStudents.length}/{students.length}</span>
            </div>

            <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
              {studLoad ? (
                <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading students...</div>
              ) : filteredStudents.length===0 ? (
                <div style={{padding:60,textAlign:'center'}}>
                  <Users size={48} color="#E5E7EB" style={{marginBottom:12}}/>
                  <div style={{fontSize:14,fontWeight:700,color:'#374151',marginBottom:6}}>No students found</div>
                  <div style={{fontSize:13,color:'#9CA3AF'}}>Click "Enroll Student" above to add students to this class</div>
                </div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:780}}>
                  <thead>
                    <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                      {['#','Roll No','Student','Degree/Year','Contact','App','Status','Actions'].map(h=>(
                        <th key={h} style={{padding:'11px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',letterSpacing:0.4,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((s,i)=>(
                      <StudentRow key={s.id} s={s} i={i}
                        onEdit={s=>{setEditStud(s);setShowStudentForm(true);}}
                        onRemove={removeStudent}
                        onStatusChange={updateStudentStatus}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          /* LIST VIEW */
          <>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
              <div>
                <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Classes</h1>
                <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>{classes.length} class{classes.length!==1?'es':''} · Click to manage students</p>
              </div>
              <button onClick={()=>{setShowClassForm(true);setEditClass(null);}}
                style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                <Plus size={16}/> Create Class
              </button>
            </div>

            <div style={{position:'relative',marginBottom:16,maxWidth:380}}>
              <Search size={13} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
              <input value={classSearchInput} onChange={e=>handleClassSearch(e.target.value)}
                placeholder="Search classes, department, teacher..." style={{...INP_STYLE,paddingLeft:28}}/>
            </div>

            {loading ? (
              <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading classes...</div>
            ) : filteredClasses.length===0 ? (
              <div style={{padding:60,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB'}}>
                <BookOpen size={48} color="#E5E7EB" style={{marginBottom:12}}/>
                <div style={{fontSize:15,fontWeight:700,color:'#374151',marginBottom:6}}>No classes yet</div>
                <div style={{fontSize:13,color:'#9CA3AF'}}>Click "Create Class" to add your first class</div>
              </div>
            ) : (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16}}>
                {filteredClasses.map(cls=>{
                  const hasTeacher = !!cls.class_teacher_id;
                  return (
                    <div key={cls.id} style={{background:'#fff',borderRadius:16,border:`1.5px solid ${hasTeacher?'#7C3AED30':'#E5E7EB'}`,overflow:'hidden'}}>
                      <div style={{padding:'16px 18px',background:hasTeacher?'linear-gradient(135deg,#7C3AED10,#4F8EF708)':'linear-gradient(135deg,#4F8EF710,transparent)',borderBottom:'1px solid #F3F4F6',cursor:'pointer'}} onClick={()=>openClass(cls)}>
                        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:8}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:800,color:'#111827',fontSize:15}}>{cls.name}</div>
                            {cls.display_name && <div style={{fontSize:12,color:'#6B7280',marginTop:1}}>{cls.display_name}</div>}
                          </div>
                          <div style={{display:'flex',gap:5}}>
                            {cls.degree && <span style={{fontSize:10,fontWeight:700,color:'#4F8EF7',background:'#EFF6FF',padding:'2px 8px',borderRadius:4}}>{cls.degree}</span>}
                            {cls.shift  && <span style={{fontSize:10,color:'#6B7280',background:'#F3F4F6',padding:'2px 8px',borderRadius:4}}>{cls.shift}</span>}
                          </div>
                        </div>
                        <div style={{fontSize:12,color:'#6B7280',display:'flex',gap:10,flexWrap:'wrap'}}>
                          {cls.department && <span>📚 {cls.department}</span>}
                          {cls.semester   && <span>📅 {cls.semester}</span>}
                          {cls.room       && <span>🏛 {cls.room}</span>}
                        </div>
                      </div>
                      <div style={{padding:'12px 18px'}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <Users size={14} color="#4F8EF7"/>
                            <span style={{fontSize:14,fontWeight:800,color:'#111827'}}>{cls.total_students||0}</span>
                            <span style={{fontSize:12,color:'#9CA3AF'}}>students</span>
                          </div>
                          <button onClick={()=>openClass(cls)}
                            style={{fontSize:12,fontWeight:700,color:'#4F8EF7',background:'#EFF6FF',padding:'5px 12px',borderRadius:6,border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                            Manage <ChevronRight size={12}/>
                          </button>
                        </div>
                        {hasTeacher ? (
                          <div style={{fontSize:12,color:'#5B21B6',background:'#F5F3FF',padding:'8px 12px',borderRadius:8,border:'1px solid #DDD6FE',display:'flex',alignItems:'center',gap:6}}>
                            <UserCheck size={13} color="#7C3AED"/>
                            <span style={{fontWeight:700}}>{cls.class_teacher_name}</span>
                            <span style={{color:'#9CA3AF',fontSize:11}}>· Class Teacher</span>
                          </div>
                        ) : (
                          <div style={{fontSize:12,color:'#92400E',background:'#FFF7ED',padding:'8px 12px',borderRadius:8,border:'1px solid #FED7AA'}}>
                            ⚠️ No class teacher — attendance cannot be marked
                          </div>
                        )}
                      </div>
                      <div style={{padding:'0 18px 14px',display:'flex',gap:8}}>
                        <button onClick={e=>{e.stopPropagation();setEditClass(cls);setShowClassForm(true);}}
                          style={{flex:1,padding:'7px 0',borderRadius:8,border:'1px solid #E5E7EB',background:'#F9FAFB',color:'#374151',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                          ✏️ Edit / Assign Teacher
                        </button>
                        <button onClick={e=>{e.stopPropagation();if(window.confirm('Delete this class?')){supabase.from('lms_classes').update({is_active:false}).eq('id',cls.id).then(loadAll);}}}
                          style={{padding:'7px 12px',borderRadius:8,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}>
                          <Trash2 size={13} color="#EF4444"/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* MODALS — rendered outside list/detail so no re-render pollution */}
        {showClassForm && (
          <ClassFormModal
            key={editClass?.id||'new'}
            editClass={editClass}
            teachers={teachers}
            institutionId={institution?.id}
            navigate={navigate}
            onSave={handleSaveClass}
            onClose={()=>{setShowClassForm(false);setEditClass(null);}}
          />
        )}
        {showStudentForm && selClass && (
          <StudentFormModal
            key={editStud?.id||'new-stud'}
            editStud={editStud}
            classId={selClass.id}
            institutionId={institution?.id}
            onSave={handleSaveStud}
            onClose={()=>{setShowStudentForm(false);setEditStud(null);}}
          />
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}